import m, {type ComponentType, type RedirectObject, type RouteResolver, type Vnode} from '@bitstillery/mithril'
import {$s} from '@bitstillery/common/app'
import {getCurrentUrl} from '@bitstillery/mithril'

;(function () {
    if (!globalThis.__SSR_MODE__) {
        const push_state = history.pushState.bind(history)
        const replace_state = history.replaceState.bind(history)

        // A pushState navigation is not a document load, so the new page inherits the scroll offset
        // of the one the visitor left. Reinstate the browser's own rule here, once, rather than in
        // every link component: landing on another path starts at the top, while history traversal
        // keeps the offset the browser recorded for that entry (`history.scrollRestoration` is left
        // on its 'auto' default).
        let last_pathname = window.location.pathname

        function scroll_to_top_on_new_path() {
            const pathname = window.location.pathname
            const is_new_page = pathname !== last_pathname
            last_pathname = pathname
            // Same path, other query params: a filter, a tab, an opened detail dialog — the visitor
            // is still looking at the page they scrolled to. An `#anchor` addresses a spot on the
            // target page, so the top is the one place it must not go.
            if (!is_new_page || window.location.hash) {
                return
            }
            window.scrollTo({behavior: 'instant', top: 0})
        }

        history.pushState = function pushState(...args: Parameters<typeof push_state>) {
            push_state(...args)
            window.dispatchEvent(new Event('pushstate'))
            window.dispatchEvent(new Event('locationchange'))
            scroll_to_top_on_new_path()
        }

        history.replaceState = function replaceState(...args: Parameters<typeof replace_state>) {
            replace_state(...args)
            window.dispatchEvent(new Event('replacestate'))
            window.dispatchEvent(new Event('locationchange'))
            scroll_to_top_on_new_path()
        }

        window.addEventListener('popstate', function () {
            window.dispatchEvent(new Event('locationchange'))
            // Back and forward are the browser's to restore; only keep the reference in sync.
            last_pathname = window.location.pathname
        })
    }
})()

type AuthGate = <C>(component: C) => C | RedirectObject

export const make_route = function make_route(auth: AuthGate, layout: ComponentType) {
    return function route_render(component: ComponentType): RouteResolver {
        return {
            onmatch: () => auth(component),
            render: (vnode: Vnode) => {
                // The empty attrs keep the vnode a child: m(layout, vnode) could mistake it for attrs.
                return m(layout, {}, vnode)
            },
        }
    }
}

export const make_route_conditional = (auth: () => ComponentType, layout: () => ComponentType): RouteResolver => ({
    onmatch: () => auth(),
    render: (vnode: Vnode) => m(layout(), vnode),
})

export function optional_auth<C>(component: C): C {
    return component
}

/** `url` may be a function, for a target built from the route's params `K`. */
export function redirect_to<K extends string = never>(url: string | ((params: Record<K, string>) => string)) {
    return {
        oninit: (vnode: m.Vnode<Record<K, string>>) => m.route.set(typeof url === 'function' ? url(vnode.attrs) : url),
        view: () => null,
    }
}

export function require_auth<C>(component: C): C | RedirectObject {
    if (!$s.identity.token && !$s.identity.user.artkey) {
        // Return redirect object - works isomorphically in both browser and SSR
        const redirect_param = encodeURIComponent(getCurrentUrl())
        const redirectPath = !redirect_param.includes('dashboard') ? `/login?redirect=${redirect_param}` : '/login'

        return m.route.redirect(redirectPath)
    }
    return component
}

export function require_no_auth<C>(component: C) {
    if ($s.identity.token) {
        // Return redirect object - works isomorphically in both browser and SSR
        return m.route.redirect('/')
    }
    return component
}
